import { H1 } from '@/components'
import { cn } from '@/lib/utils'
import { ChartNoAxesGantt, Flag, MapPin } from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'

type PreviewRow = {
  id: string
  user: string
  dateFrom: string
  dateTo?: string
  plannedStart?: string
  plannedStop?: string
  actualStart: string
  actualStop: string
  startLocation: string
  stopLocation: string
}

const previewRows: PreviewRow[] = [
  {
    id: '1',
    user: 'Ole Brumm',
    dateFrom: 'tir 21. april',
    plannedStart: '09:00',
    plannedStop: '20:30',
    actualStart: '09:28',
    actualStop: '20:42',
    startLocation: 'Oslo term.',
    stopLocation: 'Drammen syd',
  },
  {
    id: '2',
    user: 'Ole Brumm',
    dateFrom: 'tir 21. april',
    dateTo: 'ons 22. april',
    plannedStart: '07:30',
    plannedStop: '19:15',
    actualStart: '07:49',
    actualStop: '19:44',
    startLocation: 'Alnabru',
    stopLocation: 'Larvik havn',
  },
  {
    id: '3',
    user: 'Ole Brumm',
    dateFrom: 'fre 24. april',
    actualStart: '06:24',
    actualStop: '18:33',
    startLocation: 'Moss øst',
    stopLocation: 'Kristiansand',
  },
]

const tableGridStyle: CSSProperties = {
  gridTemplateColumns:
    '180px minmax(240px, 1fr) minmax(240px, 1fr) 180px 180px 140px 90px 90px',
}

const placeholderTone = {
  strong: 'bg-white text-stone-700 border-stone-200',
  muted: 'bg-stone-100 text-stone-400 border-stone-200',
}

function PlaceholderField({
  label,
  muted = false,
}: {
  label: string
  muted?: boolean
}) {
  return (
    <div
      className={cn(
        'flex h-12 items-center rounded-2xl border px-4 text-base shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]',
        muted ? placeholderTone.muted : placeholderTone.strong
      )}
    >
      {label}
    </div>
  )
}

function CellFrame({
  eyebrow,
  body,
  footer,
  className,
}: {
  eyebrow?: ReactNode
  body: ReactNode
  footer: ReactNode
  className?: string
}) {
  return (
    <div className={cn('min-h-[84px] bg-transparent px-2 py-1.5', className)}>
      <div className="min-w-0">{eyebrow}</div>
      <div className="mt-2">{body}</div>
      <div className="mt-1.5">{footer}</div>
    </div>
  )
}

function RowDateText({ row }: { row: PreviewRow }) {
  if (row.dateTo && row.dateTo !== row.dateFrom) {
    return (
      <div className="mt-1 text-sm leading-5 text-stone-500">
        <div>{row.dateFrom}</div>
        <div>{row.dateTo}</div>
      </div>
    )
  }

  return <div className="mt-1 text-sm text-stone-500">{row.dateFrom}</div>
}

function TimePill({ value }: { value: string }) {
  return (
    <div className="inline-flex items-center rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600">
      <span>Planlagt&nbsp;</span>
      <span className="font-semibold">{value}</span>
    </div>
  )
}

function LocationLine({
  icon,
  text,
  emphasis = false,
}: {
  icon: ReactNode
  text: string
  emphasis?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 truncate text-sm',
        emphasis ? 'text-stone-800' : 'text-stone-500'
      )}
    >
      {icon}
      <span className="truncate">{text}</span>
    </div>
  )
}

function VariantAStartCell({ row }: { row: PreviewRow }) {
  return (
    <CellFrame
      eyebrow={
        row.plannedStart ? <TimePill value={row.plannedStart} /> : undefined
      }
      body={
        <div className="text-[21px] font-semibold leading-none text-stone-900">
          {row.actualStart}
        </div>
      }
      footer={
        <LocationLine
          icon={<MapPin className="h-4 w-4 shrink-0 text-stone-400" />}
          text={row.startLocation}
          emphasis
        />
      }
    />
  )
}

function VariantAStopCell({ row }: { row: PreviewRow }) {
  return (
    <CellFrame
      eyebrow={
        row.plannedStop ? <TimePill value={row.plannedStop} /> : undefined
      }
      body={
        <div className="text-[21px] font-semibold leading-none text-stone-900">
          {row.actualStop}
        </div>
      }
      footer={
        <LocationLine
          icon={<Flag className="h-4 w-4 shrink-0 text-stone-400" />}
          text={row.stopLocation}
          emphasis
        />
      }
    />
  )
}

function GanttButton() {
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-600">
      <ChartNoAxesGantt className="h-4.5 w-4.5" />
    </div>
  )
}

function SelectedPreviewTable() {
  return (
    <div className="overflow-hidden rounded-[24px] bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)]">
      <div className="px-5 pt-4">
        <div className="mb-3 text-[15px] font-semibold text-stone-700">
          Uke 17
        </div>
      </div>

      <div className="overflow-x-auto px-5 pb-5">
        <div className="min-w-[1320px] rounded-[24px] bg-stone-50/70 p-3">
          <div
            className="grid gap-4 rounded-[18px] bg-[#f3f5f8] px-5 py-4 text-[15px] font-semibold uppercase tracking-[0.03em] text-stone-600"
            style={tableGridStyle}
          >
            <div>Bruker</div>
            <div>Start</div>
            <div>Stopp</div>
            <div>Lønnsmodus</div>
            <div>Ansattgruppe</div>
            <div>Diett</div>
            <div>Tillegg</div>
            <div>Godkjent av leder</div>
          </div>

          <div className="mt-2 space-y-0">
            {previewRows.map(row => (
              <div
                key={row.id}
                className="grid items-center gap-4 border-b border-stone-200/70 bg-white px-5 py-3 last:border-b-0"
                style={tableGridStyle}
              >
                <div className="flex h-full min-h-[84px] items-center gap-4">
                  <GanttButton />
                  <div>
                    <div className="font-medium text-stone-900">{row.user}</div>
                    <RowDateText row={row} />
                  </div>
                </div>

                <VariantAStartCell row={row} />
                <VariantAStopCell row={row} />

                <PlaceholderField label="Timelønn" />
                <PlaceholderField label="Velg gruppe" muted />
                <PlaceholderField label="Diett" />
                <div className="flex h-[84px] items-center justify-center">
                  <div className="rounded-2xl bg-teal-500 px-4 py-3 text-xl font-semibold text-white">
                    +
                  </div>
                </div>
                <div className="flex h-[84px] items-center justify-center text-stone-400">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-stone-300 text-2xl">
                    +
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export const WorkPeriodDateCellPreview: React.FC = () => {
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(20,184,166,0.08),_transparent_28%),linear-gradient(180deg,#f6f6f2_0%,#f3f4f6_100%)]">
      <div className="mx-auto max-w-[1600px] px-4 py-8 md:px-8 md:py-10">
        <div className="mb-6 max-w-4xl">
          <div className="mb-3 inline-flex rounded-full bg-white/85 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 shadow-sm">
            tactrl-frontend / selected direction
          </div>
          <H1 className="mb-3">Approval table preview</H1>
          <p className="text-base leading-7 text-stone-600">
            The surrounding table keeps its existing structure and styling. The
            updated preview only changes the left gantt action icon plus the
            content and layout inside the date-related cells.
          </p>
        </div>

        <SelectedPreviewTable />
      </div>
    </div>
  )
}
